import Sort from "@mui/icons-material/Sort";
import { Box, ListSubheader, MenuItem, Stack } from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import AddConditionButton from "../buttons/AddConditionButton";
import RaisedTrashIconButton from "../buttons/RaisedTrashIconButton";
import type { RuleVariable } from "../hooks/useRuleVariables";
import type { TableOption, TableSource } from "../types/configs";
import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

interface TableFieldOption {
	name: string;
	id?: string;
	databaseTableId?: string;
	screenTableId?: string;
	type: "Text" | "Number" | "Date" | "Boolean" | "Enum" | "Lookup";
}

export interface TableGroup {
	label: string;
	icon?: React.ReactElement;
	countLabel?: string;
	options: TableOption[];
	source: TableSource;
}

type SortOrder = "asc" | "desc";

export interface SortField {
	sortByDatabaseTableFieldId?: string;
	sortByScreenTableFieldId?: string;
	sortByFieldName?: string;
	sortOrder: SortOrder;
}

interface SortCondition {
	id: string;
	field: string;
	direction: SortOrder;
}

export interface SortFormState {
	sortName: string;
	databaseTableId?: string;
	screenTableId?: string;
	databaseTableVariableId?: string;
	tableName: string;
	conditions: SortField[];
}

type SortFormInternalState = {
	sortName: string;
	table: string; // Internal state uses <source>-<tableId> format for the select
	tableName: string; // Store the label separately
	conditions: SortCondition[];
};

export interface SortFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: SortFormState) => void;
	initialState?: Partial<SortFormState>;
	isSaving?: boolean;
	platformId: string | number;
	getDuplicateNameError?: (name: string) => string | undefined;
	/** Multi-row database table variables from rule state (only source for "Table Variable"). */
	ruleDatabaseMultiRowVariables?: RuleVariable[];
}

const DEFAULT_SORT_ORDER_OPTIONS: Array<{ label: string; value: SortOrder }> = [
	{ label: "Ascending (default)", value: "asc" },
	{ label: "Descending", value: "desc" }
];

const SortForm = (props: SortFormProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		isSaving = false,
		platformId,
		getDuplicateNameError,
		ruleDatabaseMultiRowVariables = [],
	} = props;

	const authHeader = useAuthHeader();

	const tableVariableGroups = useMemo((): TableGroup[] => {
		if (!ruleDatabaseMultiRowVariables.length) return [];
		return [
			{
				label: "Database Table Variable - Multi Row",
				source: "databaseVariable",
				options: ruleDatabaseMultiRowVariables.map((v) => ({
					label: v.name,
					value: v.id,
					source: "databaseVariable" as const,
				})),
			},
		];
	}, [ruleDatabaseMultiRowVariables]);

	const [sortFieldOptions, setSortFieldOptions] = useState<TableFieldOption[]>([]);

	const buildCondition = (overrides?: Partial<SortCondition>): SortCondition => ({
		id: overrides?.id ?? uuidv4(),
		field: overrides?.field ?? "",
		direction: overrides?.direction ?? DEFAULT_SORT_ORDER_OPTIONS[0].value
	});

	const sortFieldToCondition = (sf: SortField): SortCondition => buildCondition({
		field: sf.sortByDatabaseTableFieldId ?? sf.sortByScreenTableFieldId ?? "",
		direction: sf.sortOrder ?? "asc",
	});

	const [formState, setFormState] = useState<SortFormInternalState>({
		sortName: "",
		table: "",
		tableName: "",
		conditions: [buildCondition()]
	});


	const resolveInitialTable = (): string => {
		const vid = initialState?.databaseTableVariableId;
		if (!vid) return "";
		if (!ruleDatabaseMultiRowVariables.some((v) => v.id === vid)) return "";
		return `databaseVariable-${vid}`;
	};

	const mapInitialConditions = (): SortCondition[] =>
		initialState?.conditions?.length
			? initialState.conditions.map((sf) => sortFieldToCondition(sf))
			: [buildCondition()];

	const getTableFieldOptions = useCallback(
		async (tableId: string, source: TableSource) => {
			if (!tableId) {
				setSortFieldOptions([]);
				return [];
			}

			let resolvedDbTableId = tableId;
			if (source === "databaseVariable") {
				const variable = ruleDatabaseMultiRowVariables.find((v) => v.id === tableId);
				if (!variable?.source) {
					setSortFieldOptions([]);
					return [];
				}
				resolvedDbTableId = variable.source;
			}

			try {
				const endpoint =
					source === "screen"
						? apiRoutes.getScreenTableFields(tableId)
						: apiRoutes.getDatabaseTableFields(resolvedDbTableId);
				const response = await axios.get(endpoint, { headers: authHeader });
				if (response.status !== 200) {
					throw new Error(`Failed to fetch table fields: ${response.statusText}`);
				}
				const data = response.data;

				const fields: TableFieldOption[] = data.map((field: any) => ({
					id: String(field.id ?? ""),
					name: field.name,
					type: field.dataFieldType.name,
				}));

				setSortFieldOptions(fields);
			} catch (error) {
				console.error("Error fetching table fields:", error);
				setSortFieldOptions([]);
			}
		},
		[authHeader, ruleDatabaseMultiRowVariables],
	);

	useEffect(() => {
		if (!open || !platformId) return;

		const initialTable = resolveInitialTable();
		const selectedOption = tableVariableGroups
			.flatMap((group) => group.options.map((option) => ({ ...option, source: group.source })))
			.find((option) => `${option.source}-${option.value}` === initialTable);

		setFormState({
			sortName: initialState?.sortName ?? "",
			table: initialTable,
			tableName: initialState?.tableName ?? selectedOption?.label ?? "",
			conditions: mapInitialConditions(),
		});

		if (initialTable) {
			const [source, ...rest] = initialTable.split("-");
			const tableId = rest.join("-");
			void getTableFieldOptions(tableId, source as TableSource);
		} else {
			setSortFieldOptions([]);
		}
	}, [
		open,
		initialState?.sortName,
		initialState?.tableName,
		initialState?.databaseTableVariableId,
		ruleDatabaseMultiRowVariables,
	]);

	const duplicateNameError = useMemo(
		() => getDuplicateNameError?.(formState.sortName),
		[getDuplicateNameError, formState.sortName],
	);

	const isSaveDisabled =
		!formState.sortName.trim() ||
		Boolean(duplicateNameError) ||
		!formState.table ||
		formState.conditions.some((field) => !field.field || !field.direction);

	const handleSave = () => {
		const [source, ...rest] = formState.table.split("-");
		const tableId = rest.join("-");
		const selectedOption = tableVariableGroups
			.flatMap((group) => group.options.map((option) => ({ ...option, source: group.source })))
			.find((option) => `${option.source}-${option.value}` === formState.table);

		const backingDatabaseTableId =
			source === "databaseVariable"
				? ruleDatabaseMultiRowVariables.find((v) => v.id === tableId)?.source
				: undefined;

		onSave({
			sortName: formState.sortName.trim(),
			databaseTableId: source === "database" ? tableId : backingDatabaseTableId,
			screenTableId: source === "screen" ? tableId : undefined,
			databaseTableVariableId: source === "databaseVariable" ? tableId : undefined,
			tableName: selectedOption?.label ?? formState.tableName,
			conditions: formState.conditions.map(({ field, direction }) => {
				const fieldOption = sortFieldOptions.find((o) => o.id === field);
				return {
					sortByDatabaseTableFieldId: source !== "screen" ? field : undefined,
					sortByScreenTableFieldId: source === "screen" ? field : undefined,
					sortByFieldName: fieldOption?.name ?? field,
					sortOrder: direction,
				};
			})
		});
	};

	const updateSortField = (id: string, updates: Partial<Omit<SortCondition, "id">>) => {
		setFormState((previous) => ({
			...previous,
			conditions: previous.conditions.map((condition) =>
				condition.id === id ? { ...condition, ...updates } : condition
			)
		}));
	};

	const handleAddSortField = () => {
		setFormState((previous) => ({
			...previous,
			conditions: [...previous.conditions, buildCondition()]
		}));
	};

	const handleRemoveSortField = (id: string) => {
		setFormState((previous) => {
			if (previous.conditions.length === 1) {
				return previous;
			}

			return {
				...previous,
				conditions: previous.conditions.filter((condition) => condition.id !== id)
			};
		});
	};

	const handleTableChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const value = event.target.value as string;
		const [source, ...rest] = value.split("-");
		const tableId = rest.join("-");
		const selectedOption = tableVariableGroups
			.flatMap((group) => group.options.map((option) => ({ ...option, source: group.source })))
			.find((option) => `${option.source}-${option.value}` === value);

		setFormState((previous) => ({
			...previous,
			table: value,
			tableName: selectedOption?.label ?? "",
			conditions: [buildCondition()]
		}));
		getTableFieldOptions(tableId, source as TableSource);
	};

	const renderTableOptions = () => {
		if (!tableVariableGroups.length) {
			return <MenuItem disabled sx={{ fontStyle: "italic" }}>No options found</MenuItem>;
		}
		return tableVariableGroups.flatMap((group, groupIndex) => [
			<ListSubheader key={`header-${groupIndex}`} sx={{ fontWeight: "bold", color: "text.primary" }}>
				{group.label}
			</ListSubheader>,
			...(group.options ?? []).map((option) => (
				<MenuItem key={`${group.source}-${option.value}`} value={`${group.source}-${option.value}`}>
					{option.label}
				</MenuItem>
			))
		]);
	};

	const renderSortConditionRows = () => (
		<Stack spacing={2}>
			{formState.conditions.map((condition, index) => (
				<Stack
					key={condition.id}
					direction="row"
					sx={{ alignItems: "flex-start", marginBottom: 1 }}
				>
					<Box
						sx={{
							minWidth: 24,
							textAlign: "center",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "text.secondary",
							fontWeight: 500,
							paddingTop: 4
						}}
					>
						{index + 1}
					</Box>
					<Box
						sx={{
							alignItems: "center",
							display: "flex",
							borderRadius: 2,
							border: "1px solid",
							borderColor: "divider",
							backgroundColor: "background.paper",
							padding: 2,
							width: "100%"
						}}
					>
						<Stack
							direction="row"
							spacing={2}
							sx={{ alignItems: "center", justifyContent: "flex-start", width: "100%" }}
						>
							<CanvasFormTextField
								select
								fullWidth
								label="Sort Record By"
								value={formState.table ? condition.field : ""}
								onChange={(e) =>
									updateSortField(condition.id, { field: e.target.value })
								}
								disabled={!formState.table}
								width="100%"
							>
								{formState.table
									? sortFieldOptions.map((option: TableFieldOption) => (
										<MenuItem key={option.id ?? option.name} value={option.id ?? option.name}>
											{option.name}
										</MenuItem>
									))
									: []}
							</CanvasFormTextField>
							<CanvasFormTextField
								select
								fullWidth
								label="Sort Order"
								value={condition.direction}
								onChange={(e) =>
									updateSortField(condition.id, { direction: e.target.value as SortOrder })
								}
								width="100%"
							>
								{DEFAULT_SORT_ORDER_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</CanvasFormTextField>
							<RaisedTrashIconButton
								onClick={() => handleRemoveSortField(condition.id)}
								disabled={formState.conditions.length === 1}
								aria-label={`Remove sort ${index + 1}`}
							/>
						</Stack>
					</Box>
				</Stack>
			))}
		</Stack>
	);

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Sort"
			icon={<Sort />}
			iconColor="#06B097"
			gradient="linear-gradient(135deg, #02D5AB 0%, #0A7FA8 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Sort"
			width={520}
		>
			<Stack spacing={3} sx={{ p: 2 }} direction="column">
				<CanvasFormTextField
					label="Sort Name"
					value={formState.sortName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							sortName: event.target.value
						}))
					}
					placeholder="e.g., Sort 1"
					required
					width="100%"
				/>
				<CanvasFormTextField
					select
					label="Table Variable"
					width="100%"
					value={!tableVariableGroups.length ? "" : formState.table}
					onChange={handleTableChange}
					disabled={!tableVariableGroups.length}
				>
					{renderTableOptions()}
				</CanvasFormTextField>
			</Stack>
			<Box sx={{ p: 2 }}>
				{renderSortConditionRows()}
				<Box sx={{ textAlign: "center", marginTop: 2 }}>
					<AddConditionButton label="Add Sort Option" onClick={handleAddSortField} />
				</Box>
			</Box>
		</BaseForm>
	);
};

export default SortForm;
